/**
 * Layer visibility and map-selection composition for the Map Review map.
 *
 * The control pane's rows toggle what the map draws. Sites and Treatments map to
 * whole layer groups, so MapReview.vue flips their `layout.visibility`. The four
 * Feature band rows cannot: `expired` / `due_soon` / `controlled` / `unknown`
 * are one client-computed `band` property on the same two Feature layers, so
 * those rows narrow the layers' filter instead. This module owns that
 * composition so the Feature layers keep a SINGLE filter expression, and so the
 * logic is unit-testable without a browser.
 *
 * It also turns the server's resolved map selection into the layer's filter.
 * A treatment set small enough to ship as membership becomes an id filter; a
 * set too large ships as an opaque selection token whose tiles are already the
 * filtered set, so the layer carries no filter at all. A Feature set is never
 * filtered by membership either — MapReview.vue narrows the GeoJSON data itself,
 * leaving only the kind base. Where the server could prove only coarse
 * attribute clauses — a selection it could not make exact — those clauses stand
 * in as a superset of the table rows, which the control pane flags as an
 * approximate highlight; only a spec that filters nothing leaves the layer
 * unfiltered.
 *
 * Finally it symbolises the site assignment: a colour per technician, hashed
 * into a palette so the same person always draws the same hue, and the
 * data-driven expression the site layer's fill reads off the tile's
 * `assigned_user_id`.
 */

import type { ExpressionSpecification } from "maplibre-gl";
import type { MglCircleLayer } from "vue-maplibre-gl";

import { CONTROL_BANDS, DAY_MS, type ControlBand } from "@/lib/mapReviewBands";
import type { MapReviewSelection } from "@/type/api";

/** A MapLibre filter/expression clause. MapLibre renders these as expression
 * arrays; the alias names the type the layer props consume. */
export type MapFilterExpr = ExpressionSpecification;

/**
 * The type a vue-maplibre-gl layer component declares for its `filter`, which is
 * a slightly older expression union than maplibre-gl's `ExpressionSpecification`
 * (they disagree on `null` and `["boolean", …]`). The arrays composed here and in
 * the views are valid MapLibre filters; this alias names the layer prop and is
 * the one place the two typings meet, so every workbench casts at the same seam
 * rather than each inventing one.
 */
export type LayerFilter = NonNullable<
	InstanceType<typeof MglCircleLayer>["$props"]["filter"]
>;

/** Hand a composed filter to a layer prop, bridging the two type definitions
 * above. The filter is already validated against MapLibre's expression shape by
 * the module that builds it. */
export function asLayerFilter(
	expr: MapFilterExpr | undefined,
): LayerFilter | undefined {
	return expr as unknown as LayerFilter | undefined;
}

/** One control-pane row's checkbox state. Everything starts checked so the map
 * is unchanged until the operator hides something. */
export interface LayerVisibility {
	/** The four site layers: base fill/outline and the selection overlay. */
	sites: boolean;
	/** The Feature point layers as a group; overrides the per-band rows. */
	features: boolean;
	/** Feature points whose `band` is `expired`. */
	expired: boolean;
	/** Feature points whose `band` is `due_soon`. */
	due_soon: boolean;
	/** Feature points whose `band` is `controlled`. */
	controlled: boolean;
	/** Feature points whose `band` is `unknown`. */
	unknown: boolean;
	/**
	 * Whether Features the district has deactivated (`active = false`) are
	 * drawn. Off by default, so a district's deactivated locations do not clutter
	 * the map; the "Show inactive" row turns them back on. This is the
	 * district's standing decision to keep servicing the place, independent of
	 * the control bands, so it is a filter here, never a band.
	 */
	inactive: boolean;
	/**
	 * Whether the pinned revisit overlay is drawn: Feature points the server
	 * reports an active manual revisit date for, marked with a thin coloured
	 * ring above the state points. This is a whole-layer toggle, not a band or a
	 * filter — its layer never narrows the Feature points under it, so the ring
	 * still reads when the point itself is filtered out. On by default; the
	 * "Pinned revisit" row turns it off.
	 */
	pinned: boolean;
	/**
	 * Whether the observation layer is drawn: the observations matching the
	 * shared filter spec, served as an org-scoped vector tile layer. Like the
	 * pinned overlay this is a whole-layer toggle, not a band: the tile carries
	 * exactly the resolved set, so there is no client-side filter to narrow (the
	 * server applies the spec in the tile URL). On by default; the
	 * "Observations" row turns it off.
	 */
	observations: boolean;
	/** The three treatment layers. */
	treatments: boolean;
	/**
	 * The trap family's group toggle: the Trap Stations and Off-station
	 * instances layers as one whole-layer pair. Like the Features group row it
	 * overrides its two child rows — hiding it hides both trap layers
	 * regardless of their own flags. On by default; the "Traps" row turns it
	 * off.
	 */
	traps: boolean;
	/**
	 * Whether the Stations layer is drawn: one dot per kept Trap Station, at
	 * the station's active location, served as an org-scoped vector tile layer.
	 * A whole-layer toggle like the observations and treatments: the tile
	 * carries exactly the resolved set, so there is no client-side band to
	 * narrow. On by default.
	 */
	trapStations: boolean;
	/**
	 * Whether the Off-station instances layer is drawn: one dot per kept Trap
	 * Instance that has no station, at the coordinates it captured on its set.
	 * The split is where a trap result is drawn, not a judgement about it, and
	 * the tile carries only instances whose station is null so no result is
	 * drawn twice. On by default.
	 */
	trapInstances: boolean;
	/**
	 * Whether the zone layer is drawn: the district's own units as polygons,
	 * served as an org-scoped vector tile layer (`zone`). A whole-layer toggle
	 * like the observations and trap layers — the tile is the org's live zones,
	 * and the shared spec's `zones` list highlights a selection rather than
	 * narrowing this layer, because the operator has to see the zones outside
	 * the selection to change it. On by default.
	 */
	zones: boolean;
	/**
	 * Whether the temporary-zone layer is drawn: the buffers the operator
	 * materialised, held inline in the shared spec because the client already
	 * has their geometry, so there is no tile layer and nothing to wait for.
	 * A whole-layer toggle like the zones row — the buffers are what the spec
	 * is currently narrowed to, and doubling them back as a filter would only
	 * hide the shape the operator asked for — and independent of the standing
	 * zones' row: the two narrow together but are drawn and hidden separately.
	 * On by default.
	 */
	tempZones: boolean;
}

export function defaultLayerVisibility(): LayerVisibility {
	return {
		sites: true,
		features: true,
		expired: true,
		due_soon: true,
		controlled: true,
		unknown: true,
		inactive: false,
		pinned: true,
		observations: true,
		treatments: true,
		traps: true,
		trapStations: true,
		trapInstances: true,
		zones: true,
		tempZones: true,
	};
}

/** A Feature predicate that matches nothing: -1 is never a real Feature id, the
 * same sentinel the selection layers use to hide a point through its filter. The
 * Feature tile carries its id as the MVT feature id (tegola's id_fieldname), so
 * the sentinel reads ["id"] exactly as the treatment and trap layers do. */
const HIDE_ALL_FEATURES: MapFilterExpr = ["==", ["id"], -1];

/** Keeps every Feature the district has not deactivated. The tile carries
 * `active` as an integer 0/1, so a Feature is kept unless it is explicitly 0; an
 * absent property (an older cached tile) also reads as active. */
const HIDE_INACTIVE: MapFilterExpr = ["!=", ["get", "active"], 0];

/** The Feature tile's effective control-window end as epoch milliseconds
 * (`window_end_ms`), or null when nothing contributes. */
const WINDOW_END_MS = ["get", "window_end_ms"] as MapFilterExpr;

/**
 * The band predicate for a Feature tile point: `band`'s members among the
 * tile's Features, computed from the effective window end against `nowMs` and
 * the due-soon horizon exactly as controlBand bands a loaded Feature. An absent
 * window end (null) is `unknown`; a value strictly before now is `expired`; at
 * or before now plus the horizon is `due_soon`; later is `controlled`.
 */
export function featureBandPredicate(
	band: ControlBand,
	nowMs: number,
	horizonDays: number,
): MapFilterExpr {
	const end = WINDOW_END_MS;
	const horizonEnd = nowMs + horizonDays * DAY_MS;
	switch (band) {
		case "unknown":
			return ["==", end, null] as MapFilterExpr;
		case "expired":
			return ["all", ["!=", end, null], ["<", end, nowMs]] as MapFilterExpr;
		case "due_soon":
			return [
				"all",
				["!=", end, null],
				[">=", end, nowMs],
				["<=", end, horizonEnd],
			] as MapFilterExpr;
		case "controlled":
			return [
				"all",
				["!=", end, null],
				[">", end, horizonEnd],
			] as MapFilterExpr;
	}
}

/**
 * The Feature point's fill, by the band predicates themselves: a null window end
 * is `unknown`, strictly before now is `expired`, at or before now plus the
 * horizon is `due_soon`, and anything later is `controlled`. Building the fill
 * from the same featureBandPredicate the visibility filter uses is what keeps
 * the colour and the filter from drifting — there is one statement of where each
 * band begins.
 */
export function featureBandColorExpr(
	nowMs: number,
	horizonDays: number,
	colors: Record<ControlBand, string>,
): MapFilterExpr {
	return [
		"case",
		featureBandPredicate("unknown", nowMs, horizonDays),
		colors.unknown,
		featureBandPredicate("expired", nowMs, horizonDays),
		colors.expired,
		featureBandPredicate("due_soon", nowMs, horizonDays),
		colors.due_soon,
		colors.controlled,
	] as MapFilterExpr;
}

/**
 * The Feature layers' visibility as one predicate, ANDed into their existing
 * filters. Returns undefined when nothing narrows, which MapLibre reads as "no
 * filter" — so the map keeps its previous filter until a row hides.
 *
 * The group row wins: hiding Features hides every Feature point regardless of
 * the per-band rows. Otherwise the visible bands are kept by membership, and
 * hiding all four band rows hides every point. Inactive Features are dropped
 * unless the "Show inactive" row is checked, independent of the bands.
 *
 * The band is computed here from the tile's `window_end_ms` — the Feature tile
 * cannot carry a client-computed `band`, so the same banding the legend does in
 * controlBand is expressed over the tile property, against the caller's clock
 * and due-soon horizon.
 */
export function featureVisibilityFilter(
	visibility: LayerVisibility,
	nowMs: number,
	horizonDays: number,
): MapFilterExpr | undefined {
	if (!visibility.features) return HIDE_ALL_FEATURES;
	const bands = CONTROL_BANDS.filter((band) => visibility[band]);
	if (bands.length === 0) return HIDE_ALL_FEATURES;
	const parts: MapFilterExpr[] = [];
	if (bands.length !== CONTROL_BANDS.length) {
		// A lone checked band needs no `any` wrapper (and reads the same to
		// MapLibre); several are OR'd together.
		parts.push(
			bands.length === 1
				? featureBandPredicate(bands[0], nowMs, horizonDays)
				: ([
						"any",
						...bands.map((band) =>
							featureBandPredicate(band, nowMs, horizonDays),
						),
					] as MapFilterExpr),
		);
	}
	if (!visibility.inactive) parts.push(HIDE_INACTIVE);
	if (parts.length === 0) return undefined;
	if (parts.length === 1) return parts[0];
	return ["all", ...parts];
}

/**
 * AND-combine filter expressions, dropping the ones that are unset (an unset
 * filter passes everything). Returns undefined when nothing is active, which
 * MapLibre reads as "no filter".
 */
export function andExpr(
	...exprs: (MapFilterExpr | null | undefined)[]
): MapFilterExpr | undefined {
	const parts = exprs.filter(Boolean) as MapFilterExpr[];
	if (parts.length === 0) return undefined;
	if (parts.length === 1) return parts[0];
	return ["all", ...parts];
}

/** A treatment predicate that matches nothing: -1 is never a real tile feature
 * id, so the MVT `["id"]` form hides every treatment. */
const HIDE_ALL_TREATMENTS: MapFilterExpr = ["==", ["id"], -1];

/** A trap predicate that matches nothing, the same sentinel the treatment layer
 * uses to hide every point through its filter. */
const HIDE_ALL_TRAPS: MapFilterExpr = ["==", ["id"], -1];

/**
 * A trap tile layer's filter for one resolved selection. The station and
 * off-station layers are MVT layers whose feature id is the row's own id, so the
 * selection is membership of the shipped ids — the same shape the treatment
 * layer uses. A selection with no ids ships nothing to narrow (the tile is
 * already the org's, or the area's, resolved set).
 */
export function trapSelectionFilter(
	sel: MapReviewSelection | null,
): MapFilterExpr | undefined {
	if (!sel?.ids) return undefined;
	return sel.ids.length > 0
		? ["in", ["id"], ["literal", sel.ids]]
		: HIDE_ALL_TRAPS;
}

/**
 * The treatment tile layer's filter for one resolved selection.
 *
 * A non-null selection token means the tiles are already the filtered set — the
 * token rides in the tile URL and Postgres applied the selection server-side —
 * so the layer carries no filter. A non-null id set is exact membership (an
 * empty set keeps nothing and draws nothing). Otherwise the server echoes the
 * coarse attribute clauses (product and period), the only way the map can
 * reproduce a selection it could not make exact; they are applied together and
 * mean nothing at all when the spec filters nothing.
 *
 * Returns undefined for "no filter": either this spec filters nothing or the
 * tiles are already the filtered set, which MapLibre reads as no filter.
 */
export function treatmentSelectionFilter(
	sel: MapReviewSelection | null,
): MapFilterExpr | undefined {
	if (!sel) return undefined;
	if (sel.selection_token) return undefined;
	if (sel.ids) {
		if (sel.ids.length === 0) return HIDE_ALL_TREATMENTS;
		return ["in", ["id"], ["literal", sel.ids]];
	}
	return andExpr(
		sel.product_ids
			? ([
					"in",
					["get", "product_id"],
					["literal", sel.product_ids],
				] as MapFilterExpr)
			: null,
		sel.start_min != null
			? ([">=", ["get", "start_ms"], sel.start_min] as MapFilterExpr)
			: null,
		sel.start_max != null
			? (["<=", ["get", "start_ms"], sel.start_max] as MapFilterExpr)
			: null,
	);
}

/**
 * A Feature tile layer's filter for one resolved selection. The Feature tile
 * carries the Feature's own id as the MVT feature id, so an exact membership
 * selection narrows by ["id"] (the same form the treatment and trap layers use).
 * A non-null selection token means the tiles are already the filtered set — the
 * token rides in the tile URL and Postgres applied the spec — so the layer
 * carries no filter. A selection the server could not make exact ships only its
 * coarse clauses; the Feature tile has none, so it draws unnarrowed.
 *
 * Returns undefined for "no filter", which MapLibre reads as no filter.
 */
export function featureTileSelectionFilter(
	sel: MapReviewSelection | null,
): MapFilterExpr | undefined {
	if (!sel) return undefined;
	if (sel.selection_token) return undefined;
	if (sel.ids) {
		return sel.ids.length > 0
			? (["in", ["id"], ["literal", sel.ids]] as MapFilterExpr)
			: HIDE_ALL_FEATURES;
	}
	return undefined;
}

/** A zone predicate that matches nothing: -1 is never a real zone id, the same
 * sentinel the Feature layers hide a point with. The zone tile carries its id
 * as the MVT feature id (tegola's id_fieldname), so the sentinel reads ["id"]
 * exactly as the treatment, trap and Feature layers do. */
const HIDE_ALL_ZONES: MapFilterExpr = ["==", ["id"], -1];

/**
 * The zone tile layer's filter for the shared spec's selected zones. The zone
 * tile carries the zone's own id as the MVT feature id, so the highlight is
 * plain id membership of `spec.zones`: the base fill and outline draw every live
 * zone the district has, and this layer adds the selected ones over them. With
 * nothing selected there is nothing to add, so the layer hides itself rather
 * than drawing every zone twice.
 */
export function zoneSelectionFilter(selected: number[]): MapFilterExpr {
	return selected.length > 0
		? ["in", ["id"], ["literal", selected]]
		: HIDE_ALL_ZONES;
}

/**
 * The site fabric's neutral, low-contrast fill, so Features and Treatments stay
 * legible on top of it and the streets between parcels read as part of the fabric
 * rather than as holes. It is also the fallback the assignment expression paints
 * an unassigned Site with (`siteAssignmentColorExpr`): a Site nobody has been
 * sent to keeps the fabric's own neutral, so the map reads as a plan of who is
 * working where only where a plan exists.
 *
 * It lives here, beside the palettes, rather than in the view that first drew
 * it: the lab trap view draws the same fabric under its own layers, and one
 * spelling is what keeps the two maps' base layers the same picture.
 */
export const SITE_FILL_COLOR = "#94a3b8";

/**
 * The `site` tegola layer's `min_zoom`, which the map's own site layer must
 * match: below it the tile carries no fabric at all, so a layer that draws at a
 * lower zoom paints nothing. The workbench frames the district with fitBounds at
 * ~z8.8 on a 1400x900 viewport, which requests z8 tiles, so the fabric is the
 * base layer from the district view up. Measured: the district's four z8 tiles
 * total 2.30 MB raw and its busiest tile is 2.35 MB raw / 0.72 MB gzip.
 */
export const SITE_MIN_ZOOM = 8;

/**
 * The trap family's two dot colours: a deep teal for the standing stations and a
 * violet for the instances, both distinct from the Feature, Treatment and
 * Observation hues, so where a trap result is drawn is legible at a glance.
 *
 * They are stated here rather than in one view because both maps draw the trap
 * family — the Review Workbench's stations and off-station instances, and the
 * lab trap view's stations and its all-instances layer — and a station must be
 * the same teal on both.
 */
export const TRAP_STATION_COLOR = "#0d9488";
export const TRAP_INSTANCE_COLOR = "#7c3aed";

/**
 * Okabe–Ito qualitative palette, less its black: the site fabric is drawn under
 * every record layer, so a technician's colour has to hold its own hue at a low
 * fill opacity without reading as a hole in the basemap, which is what the
 * palette's swatch of black does.
 */
export const ASSIGNED_SITE_PALETTE: string[] = [
	"#E69F00",
	"#56B4E9",
	"#009E73",
	"#F0E442",
	"#0072B2",
	"#D55E00",
	"#CC79A7",
];

/**
 * How far each successive technician id steps through the palette. It is
 * coprime with the palette's length, which is what makes the guarantee below
 * hold (see `assignedSiteColor`).
 */
const ASSIGNED_SITE_STRIDE = 3;

/**
 * A stable colour for one technician id.
 *
 * The id walks the palette in fixed strides rather than being run through a
 * mixing hash: two technicians working the same district are usually adjacent
 * ids, and a general hash gives no promise at all about how adjacent ids map —
 * it collides onto the same hue for roughly one pair in seven. A stride coprime
 * with the palette's length cannot: consecutive ids always land on different
 * hues, and the first seven ids take all seven of them. The same id always
 * gives the same colour, in every session and on every surface.
 */
export function assignedSiteColor(userID: number): string {
	const index =
		(Math.trunc(Math.abs(userID)) * ASSIGNED_SITE_STRIDE) %
		ASSIGNED_SITE_PALETTE.length;
	return ASSIGNED_SITE_PALETTE[index];
}

/**
 * The site layer's data-driven fill: a `match` on the tile's
 * `assigned_user_id` (the assignment join in etc/tegola.toml) mapping each
 * known technician to its stable colour, falling back to `unassigned` — the
 * flat style the layer had before assignments existed — for a Site with no
 * assignment and for a technician the caller did not name. An empty list names
 * no one, so every Site keeps the flat style; it is spelled as a match with an
 * impossible label (-1 is never a user id) because MapLibre's `match` takes at
 * least one label.
 */
export function siteAssignmentColorExpr(
	userIDs: number[],
	unassigned: string,
): MapFilterExpr {
	const pairs: (number | string)[] = [];
	for (const id of userIDs) pairs.push(id, assignedSiteColor(id));
	if (pairs.length === 0) {
		return [
			"match",
			["get", "assigned_user_id"],
			-1,
			unassigned,
			unassigned,
		] as unknown as MapFilterExpr;
	}
	// The label/output pairs are spread from a list whose length the type system
	// cannot know, and MapLibre's spec wants a fixed tuple — hence the double
	// assertion, the same escape the view's own hand-built paint expressions use.
	return [
		"match",
		["get", "assigned_user_id"],
		...pairs,
		unassigned,
	] as unknown as MapFilterExpr;
}

/**
 * The population view's bivariate palette: the operator's "original"
 * teal/grey/orange, `POPULATION_BIV[slope][level]` — rows falling, stable,
 * rising; columns low, medium, high, so a cell's class is `slope * 3 + level`
 * (the contract's index). It is stated here, beside the other map palettes,
 * rather than in the rail panel, because the workbench map paints the same
 * nine classes when the view is sent to the map: one spelling keeps the map,
 * the key and the class swatches the same picture.
 *
 * The row order is also a lightness story — every row runs light to dark left
 * to right, so level reads off the fill whatever the slope — which is why the
 * grid is not re-ordered to match the screen.
 */
export const POPULATION_BIV: [string[], string[], string[]] = [
	["#d6ecec", "#8fc6c8", "#3f8e92"], // falling
	["#ececec", "#b9c3cb", "#7d8a96"], // stable
	["#f7ddcc", "#eda67f", "#c15a2e"], // rising
];

/**
 * The colour a cell with no class draws. Level or slope was missing — too few
 * weeks for a fit, or no analysed samples — so the estimator proved no class
 * and the cell is stated as unknown rather than painted as one it might not be.
 */
export const POPULATION_NEUTRAL_COLOR = "#e5e7eb";

/**
 * The population fill's opacity. The layer is sent to the workbench map as an
 * overlay under the record layers, not as a standalone picture: the site
 * fabric, the trap dots and the selected record have to stay legible through
 * it, so the nine classes are drawn as a wash rather than opaque (the rail's
 * own map draws them opaque, where they ARE the picture).
 */
export const POPULATION_FILL_OPACITY = 0.7;

/**
 * The population cells' fill, a `match` over the feature's `class` property —
 * the nine palette colours on labels 0..8, and the neutral on -1 (the value
 * the view writes for a cell the estimator could not classify). The labels are
 * numbers, which is what MapLibre's `match` accepts for a numeric input, and
 * the fallback repeats the neutral so an unexpected value cannot paint a cell
 * a class it has no colour for.
 */
export function populationFillColorExpr(): MapFilterExpr {
	const labels: (number | string)[] = [];
	POPULATION_BIV.forEach((row, slope) => {
		row.forEach((color, level) => {
			labels.push(slope * 3 + level, color);
		});
	});
	labels.push(-1, POPULATION_NEUTRAL_COLOR);
	return [
		"match",
		["get", "class"],
		...labels,
		POPULATION_NEUTRAL_COLOR,
	] as unknown as MapFilterExpr;
}

/**
 * The waste view's bivariate palette: the operator's approved "original"
 * teal/grey/orange, `WASTE_BIV[conversionRow][volumeColumn]` — rows high,
 * middling and low conversion (the share of a cell's visits that applied a
 * treatment), columns few, medium and many visits, so a cell's class is
 * `conversionRow * 3 + volumeColumn`. It is stated here, beside the population
 * palette, because the workbench map paints the same nine classes when the
 * waste view is sent to the map: one spelling keeps the rail's map, the key and
 * the sent layer the same picture.
 */
export const WASTE_BIV: [string[], string[], string[]] = [
	["#d6ecec", "#8fc6c8", "#3f8e92"], // high conversion
	["#ececec", "#b9c3cb", "#7d8a96"], // middling
	["#f7ddcc", "#eda67f", "#c15a2e"], // low conversion — the waste corner
];

/**
 * The fill for a thin cell: fewer than five visits cannot be judged, so it is
 * drawn pale and never coloured. The same rule the rail's map draws with.
 */
export const WASTE_THIN_COLOR = "#eef1f5";

/**
 * The waste fill's opacity. Like the population layer it is a wash under the
 * record layers, so the site fabric and the selected record stay legible
 * through it.
 */
export const WASTE_FILL_OPACITY = 0.7;

/**
 * The waste cells' fill, a `match` over the feature's `class` property — the
 * nine palette colours on labels 0..8, and the pale thin-cell tint on -1 (the
 * value the view writes for a cell below the five-visit floor). Labels are
 * numbers, which is what MapLibre's `match` accepts for a numeric input, and
 * the fallback repeats the pale tint so an unexpected value cannot paint a cell
 * a class it has no colour for.
 */
export function wasteFillColorExpr(): MapFilterExpr {
	const labels: (number | string)[] = [];
	WASTE_BIV.forEach((row, conversion) => {
		row.forEach((color, volume) => {
			labels.push(conversion * 3 + volume, color);
		});
	});
	labels.push(-1, WASTE_THIN_COLOR);
	return [
		"match",
		["get", "class"],
		...labels,
		WASTE_THIN_COLOR,
	] as unknown as MapFilterExpr;
}
